import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

interface Page {
  id: number;
  title: string;
  content: string;
  order: number;
  book_id: number;
  created_at: string;
  updated_at: string;
}

interface Book {
  id: number;
  title: string;
  data: Record<string, any>;
  created_at: string;
  updated_at: string;
  pages: Page[];
}

const BookView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [book, setBook] = useState<Book | null>(null);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    fetchBook();
  }, [id]);

  const fetchBook = async () => {
    try {
      const response = await fetch(`/api/books/${id}`);
      const data = await response.json();
      setBook(data);
    } catch (error) {
      console.error('Error fetching book:', error);
    }
  };

  const handleCreatePage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPageTitle.trim() || !book) return;

    try {
      const response = await fetch('/api/pages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page: {
            title: newPageTitle,
            content: '',
            book_id: book.id,
            order: book.pages.length,
          },
        }),
      });

      if (response.ok) {
        const newPage = await response.json();
        setBook({
          ...book,
          pages: [...book.pages, newPage],
        });
        setNewPageTitle('');
      } else {
        console.error('Error creating page:', await response.json());
      }
    } catch (error) {
      console.error('Error creating page:', error);
    }
  };

  const handleEditPage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPage || !editTitle.trim() || !book) return;

    try {
      const response = await fetch(`/api/pages/${editingPage.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page: {
            title: editTitle,
            content: editContent,
          },
        }),
      });

      if (response.ok) {
        const updatedPage = await response.json();
        setBook({
          ...book,
          pages: book.pages.map((page) => (page.id === updatedPage.id ? updatedPage : page)),
        });
        setEditingPage(null);
        setEditTitle('');
        setEditContent('');
      } else {
        console.error('Error updating page:', await response.json());
      }
    } catch (error) {
      console.error('Error updating page:', error);
    }
  };

  const handleDeletePage = async (pageId: number) => {
    if (!confirm('Are you sure you want to delete this page?') || !book) return;

    try {
      const response = await fetch(`/api/pages/${pageId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setBook({
          ...book,
          pages: book.pages.filter((page) => page.id !== pageId),
        });
      } else {
        console.error('Error deleting page:', await response.json());
      }
    } catch (error) {
      console.error('Error deleting page:', error);
    }
  };

  const startEditing = (page: Page) => {
    setEditingPage(page);
    setEditTitle(page.title);
    setEditContent(page.content);
  };

  const handleReorderPages = async (pageId: number, newOrder: number) => {
    if (!book) return;

    try {
      const response = await fetch(`/api/pages/${pageId}/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order: newOrder }),
      });

      if (response.ok) {
        const updatedPage = await response.json();
        const updatedPages = [...book.pages];
        const oldIndex = updatedPages.findIndex((p) => p.id === pageId);
        updatedPages.splice(oldIndex, 1);
        updatedPages.splice(newOrder, 0, updatedPage);
        setBook({ ...book, pages: updatedPages });
      } else {
        console.error('Error reordering page:', await response.json());
      }
    } catch (error) {
      console.error('Error reordering page:', error);
    }
  };

  if (!book) {
    return <div className="p-4">Loading...</div>;
  }

  console.log(book);

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{book.title}</h1>
        <button
          onClick={() => navigate('/books')}
          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
        >
          Back to Books
        </button>
      </div>

      <form onSubmit={handleCreatePage} className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={newPageTitle}
            onChange={(e) => setNewPageTitle(e.target.value)}
            placeholder="Enter page title"
            className="flex-1 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Add Page
          </button>
        </div>
      </form>

      <div className="space-y-4">
        {book.pages.map((page, index) => (
          <div key={page.id} className="border rounded-lg p-4">
            {editingPage?.id === page.id ? (
              <form onSubmit={handleEditPage} className="space-y-4">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Page title"
                />
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={5}
                  placeholder="Page content"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingPage(null);
                      setEditTitle('');
                      setEditContent('');
                    }}
                    className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex justify-between items-start mb-2">
                  <h2 className="text-xl font-semibold">{page.title}</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEditing(page)}
                      className="px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePage(page.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="prose max-w-none">
                  {page.content || <p className="text-gray-500 italic">No content yet</p>}
                </div>
                <div className="flex gap-2 mt-4">
                  {index > 0 && (
                    <button
                      onClick={() => handleReorderPages(page.id, index - 1)}
                      className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                      Move Up
                    </button>
                  )}
                  {index < book.pages.length - 1 && (
                    <button
                      onClick={() => handleReorderPages(page.id, index + 1)}
                      className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                      Move Down
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookView;
